import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Hairline,
  Icon,
  Input,
  Select,
  Tooltip,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import { CompanyLocationsList } from "../components/CompanyLocationsList.js";
import {
  CompanyApprovalStatusValues,
  CompanyPaymentTermsValues,
  CompanyTierValues,
  type CompanyApprovalStatus,
  type CompanyDTO,
  type CompanyDetailDTO,
  type CompanyPaymentTerms,
  type CompanyTier,
} from "@wmart/shared-types";

interface CompanyFormValues {
  name: string;
  slug: string;
  vat_number: string;
  vat_country: string;
  default_tier: CompanyTier;
  approval_status: CompanyApprovalStatus;
  payment_terms: CompanyPaymentTerms;
  // Form input is a string (always coerced via Number on submit) — empty
  // string represents "no credit limit" → wire as `null`.
  credit_limit_minor: string;
  // P17 — per-company COD eligibility gate. Defaults to false at the
  // DB-default boundary; explicit `true` only when admin opts in.
  cod_enabled: boolean;
}

const blankDefaults: CompanyFormValues = {
  name: "",
  slug: "",
  vat_number: "",
  vat_country: "GB",
  default_tier: "standard",
  approval_status: "pending",
  payment_terms: "immediate",
  credit_limit_minor: "",
  cod_enabled: false,
};

/**
 * Helper trigger for the inline info-hint icon. Wraps a focusable button so
 * the surrounding `<Tooltip>` can attach `aria-describedby` and surface the
 * hint on hover + focus.
 */
function InfoTrigger() {
  return (
    <button
      type="button"
      aria-label="More info"
      className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-warning text-ivory align-middle focus-visible:shadow-focus outline-none"
    >
      <Icon name="info" size={14} aria-hidden />
    </button>
  );
}

/**
 * Single page that handles both `/companies/new` and `/companies/:id`,
 * discriminated by `useParams<{id?}>()`.
 *
 * The company entity has no address columns — addresses live in
 * `address_json` on `company_locations` (Plan 08 T12 carry-forward).
 * This form therefore has zero address fields. Addresses are managed via
 * the nested locations sub-list (`<CompanyLocationsList>` rendered below
 * in edit mode), which navigates to `<CompanyLocationEdit>` for actual
 * address input.
 */
export function CompanyEdit() {
  const { id } = useParams<{ id?: string }>();
  const isNew = id === undefined;
  const navigate = useNavigate();
  const { notify, notifyProblem } = useToast();
  const [detail, setDetail] = useState<CompanyDetailDTO | null>(null);
  const [loadError, setLoadError] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm<CompanyFormValues>({
    defaultValues: blankDefaults,
  });

  const refetch = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.get<CompanyDetailDTO>(
        `/v1/admin/companies/${id}`,
      );
      setDetail(data);
      setLoadError(false);
      reset({
        name: data.name,
        slug: data.slug,
        vat_number: data.vat_number ?? "",
        vat_country: data.vat_country,
        default_tier: data.default_tier,
        approval_status: data.approval_status,
        payment_terms: data.payment_terms,
        credit_limit_minor:
          data.credit_limit_minor === null
            ? ""
            : String(data.credit_limit_minor),
        cod_enabled: data.cod_enabled,
      });
    } catch (err) {
      setLoadError(true);
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to load company");
    }
  }, [id, reset, notify, notifyProblem]);

  useEffect(() => {
    if (!isNew) void refetch();
  }, [isNew, refetch]);

  async function onSubmit(values: CompanyFormValues) {
    const trimmedVat = values.vat_number.trim();
    const creditLimitMinor =
      values.credit_limit_minor.trim() === ""
        ? null
        : Number(values.credit_limit_minor);
    if (
      creditLimitMinor !== null &&
      (!Number.isInteger(creditLimitMinor) || creditLimitMinor < 0)
    ) {
      notify("error", "Credit limit must be a non-negative integer (pence)");
      return;
    }

    try {
      if (isNew) {
        const body = {
          name: values.name,
          slug: values.slug,
          vat_number: trimmedVat === "" ? null : trimmedVat,
          vat_country: values.vat_country.toUpperCase(),
          default_tier: values.default_tier,
          approval_status: values.approval_status,
          payment_terms: values.payment_terms,
          credit_limit_minor: creditLimitMinor,
          cod_enabled: values.cod_enabled,
        };
        const created = await api.post<CompanyDTO>(
          "/v1/admin/companies",
          body,
        );
        notify("success", "Company created");
        navigate(`/companies/${created.id}`);
      } else if (id) {
        // Edit mode: slug is read-only (see disclosure below); send everything
        // else. PATCH body uses `null` to clear nullable columns explicitly.
        // P17-T11: cod_enabled is always sent; service-level no-op short-
        // circuits avoid spurious audit emission when value is unchanged.
        const body = {
          name: values.name,
          vat_number: trimmedVat === "" ? null : trimmedVat,
          vat_country: values.vat_country.toUpperCase(),
          default_tier: values.default_tier,
          approval_status: values.approval_status,
          payment_terms: values.payment_terms,
          credit_limit_minor: creditLimitMinor,
          cod_enabled: values.cod_enabled,
        };
        await api.patch<CompanyDTO>(`/v1/admin/companies/${id}`, body);
        notify("success", "Company saved");
        await refetch();
      }
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Save failed");
    }
  }

  if (loadError && !detail) {
    return (
      <div className="flex flex-col gap-4">
        <Link
          to="/companies"
          className="text-body-sm text-accent hover:underline"
        >
          ← Back to companies
        </Link>
        <p className="text-body-sm text-danger">
          Company could not be loaded.
        </p>
      </div>
    );
  }

  if (!isNew && !detail) {
    return <p className="text-body-sm text-ink-500">Loading…</p>;
  }

  return (
    <div className="flex max-w-4xl flex-col gap-6">
      <Link
        to="/companies"
        className="text-body-sm text-accent hover:underline"
      >
        ← Back to companies
      </Link>

      <h1 className="font-serif text-display-sm text-ink-900">
        {isNew ? "New company" : detail?.name || "Company"}
      </h1>

      <Card className="p-6">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          <Input
            label="Name"
            error={errors.name?.message}
            {...register("name", {
              required: "Name is required",
              maxLength: { value: 200, message: "Max 200 characters" },
            })}
          />

          <Input
            label="Slug"
            disabled={!isNew}
            className="font-mono"
            helper={
              !isNew
                ? "Slug is read-only after creation to preserve deep links."
                : undefined
            }
            error={errors.slug?.message}
            {...register("slug", {
              required: "Slug is required",
              maxLength: { value: 120, message: "Max 120 characters" },
              pattern: {
                value: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                message:
                  "Slug must be kebab-case (lowercase letters, digits, hyphens)",
              },
            })}
          />

          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                label="VAT number"
                placeholder="(optional)"
                error={errors.vat_number?.message}
                {...register("vat_number", {
                  maxLength: { value: 64, message: "Max 64 characters" },
                })}
              />
            </div>
            <div className="w-32">
              <Input
                label="VAT country"
                placeholder="GB"
                maxLength={2}
                className="uppercase"
                error={errors.vat_country?.message}
                {...register("vat_country", {
                  required: "VAT country is required",
                  pattern: {
                    value: /^[A-Za-z]{2}$/,
                    message: "ISO 2-letter country code",
                  },
                })}
              />
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <Select
                label="Default tier"
                {...register("default_tier", { required: true })}
              >
                {CompanyTierValues.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex-1">
              <Select
                label="Approval status"
                {...register("approval_status", { required: true })}
              >
                {CompanyApprovalStatusValues.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex-1">
              <Select
                label={
                  <span className="inline-flex items-center gap-1">
                    Payment terms
                    <Tooltip content="Stored only — net_30 / net_60 are enforced from V1.5 once Stripe Invoicing ships. V1.0 checkout always charges immediately regardless of this value.">
                      <InfoTrigger />
                    </Tooltip>
                  </span>
                }
                {...register("payment_terms", { required: true })}
              >
                {CompanyPaymentTermsValues.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <Input
            type="number"
            min={0}
            step={1}
            label={
              <span className="inline-flex items-center gap-1">
                Credit limit (pence)
                <Tooltip content="Stored only — enforced from V1.5 once Stripe Invoicing + Net 30/60 ship. V1.0 checkout does not gate on outstanding-balance vs limit.">
                  <InfoTrigger />
                </Tooltip>
              </span>
            }
            placeholder="(optional)"
            helper="Minor units (pence). Leave blank for no limit."
            {...register("credit_limit_minor")}
          />

          {/* ----- Payment methods (P17 — COD eligibility toggle) ----- */}
          <fieldset className="flex flex-col gap-2 rounded-sm border border-ink-200 p-4">
            <legend className="px-1 text-overline font-semibold uppercase tracking-[0.014em] text-ink-500">
              Payment methods
            </legend>
            <p className="text-caption text-ink-500">
              Payment <strong>terms</strong> above (immediate / net 30 / net 60)
              are the company&rsquo;s billing arrangement — one per company.
              Payment <strong>methods</strong> the buyer chooses at checkout
              (card / bank transfer) are set per ship-to location in the
              Locations list below. Cash on Delivery is a company-wide toggle:
            </p>
            <label className="flex items-start gap-3 text-body-sm text-ink-700">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded-sm border-ink-300 text-accent focus-visible:shadow-focus outline-none"
                {...register("cod_enabled")}
              />
              <span className="flex flex-col">
                <span className="font-medium text-ink-900">
                  Enable Cash on Delivery
                </span>
                <span className="text-caption text-ink-500">
                  Allows customers at this company to pay in cash on
                  delivery. The COD radio appears at checkout (gated
                  server-side) and the &lsquo;cod&rsquo; payment-terms
                  override becomes available on quotes.
                </span>
              </span>
            </label>
          </fieldset>

          <Hairline />

          <div>
            <Button
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {isNew ? "Create" : "Save"}
            </Button>
          </div>
        </form>
      </Card>

      {!isNew && id && detail ? (
        <CompanyLocationsList
          companyId={id}
          locations={detail.locations}
          onSuccess={refetch}
        />
      ) : null}
    </div>
  );
}
